import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory'
import { ApolloClient } from 'apollo-client'
import { ApolloLink, split } from 'apollo-link'
import { setContext } from 'apollo-link-context'
import { onError } from 'apollo-link-error'
import { HttpLink } from 'apollo-link-http'
import { createPersistedQueryLink } from 'apollo-link-persisted-queries'
import { WebSocketLink } from 'apollo-link-ws'
import { createUploadLink } from 'apollo-upload-client'
import { getMainDefinition } from 'apollo-utilities'
import { MessageTypes, SubscriptionClient } from 'subscriptions-transport-ws'
import introspectionQueryResultData from './graphql/_fragmentTypes.json'
import store from './store'

const REFRESH_TIMER_MS = 5 * 60 * 1000

let refreshTimer = null

function getAuth () {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem('apollo-token')
  // return the headers to the context so httpLink can read them
  return token ? `Bearer ${token}` : ''
}

function restartWebsockets (wsClient) {
  // Copy current operations
  const operations = Object.assign({}, wsClient.operations)

  // Close connection
  wsClient.close(true)

  // Open a new one
  wsClient.connect()

  // Push all current operations to the new connection
  Object.keys(operations).forEach(id => {
    wsClient.sendMessage(
      id,
      MessageTypes.GQL_START,
      operations[id].options
    )
  })
}

// Create the apollo client
export default function createApolloClient ({ ssr, base, endpoints, persisting, subscriptions }) {
  let wsClient

  let link = new HttpLink({
    // You should use an absolute URL here
    uri: base + endpoints.graphql
  })

  // HTTP Auth header injection
  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      authorization: getAuth()
    }
  }))

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors && process.env.NODE_ENV === 'development') {
      graphQLErrors.map(({ message, locations, path }) =>
        console.error('Unexpected error: ' + message)
      )

      // TODO only in production? still quite annoying to lose all form data...
      // router.push({
      //   name: 'home'
      // })
    }

    if (networkError) {
      if (networkError.statusCode === 401 || networkError.statusCode === 403) { // token expired or blacklisted
        store.commit('auth/logout')

        window.location.reload()
      } else {
        // unknown error or server down
        alert('Er is iets fout gegaan. Probeer het later opnieuw.')
      }
    }
  })

  // Keeps track of long it was ago that the last request was sent. If enough time has passed, reset the store so that the page stays up to date without refreshing
  const refreshMiddleware = new ApolloLink((operation, forward) => {
    clearTimeout(refreshTimer)

    refreshTimer = setTimeout(() => {
      apolloClient.resetStore()
    }, REFRESH_TIMER_MS)

    return forward(operation)
  })

  // Concat all the http link parts
  link = authLink.concat(link)
  link = errorLink.concat(link)
  link = refreshMiddleware.concat(link)

  if (persisting) {
    link = createPersistedQueryLink().concat(link)
  }

  // Fragment matcher for union types
  const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData
  })

  // Apollo cache
  const cache = new InMemoryCache({
    fragmentMatcher
  })

  if (!ssr) {
    // If on the client, recover the injected state
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-underscore-dangle
      const state = window.__APOLLO_STATE__
      if (state) {
        // If you have multiple clients, use `state.<client_id>`
        cache.restore(state.defaultClient)
      }
    }

    // File upload
    const uploadLink = authLink.concat(createUploadLink({
      uri: base + endpoints.graphql
    }))

    // using the ability to split links, you can send data to each link
    // depending on what kind of operation is being sent
    link = split(
      operation => operation.getContext().upload,
      uploadLink,
      link
    )

    // Web socket
    if (subscriptions) {
      wsClient = new SubscriptionClient(base.replace(/^https?/i, 'ws' + (process.env.NODE_ENV === 'production' ? 's' : '')) +
        endpoints.subscription, {
        reconnect: true,
        connectionParams: () => ({
          'Authorization': getAuth()
        })
      })

      // Create the subscription websocket link
      const wsLink = new WebSocketLink(wsClient)

      link = split(
        // split based on operation type
        ({ query }) => {
          const { kind, operation } = getMainDefinition(query)
          return kind === 'OperationDefinition' &&
            operation === 'subscription'
        },
        wsLink,
        link
      )
    }
  } else {
    // On the server, we don't want WebSockets and Upload links
  }

  const apolloClient = new ApolloClient({
    link,
    cache,
    // Additional options
    ...(ssr ? {
      // Set this on the server to optimize queries when SSR
      ssrMode: true
    } : {
      // This will temporary disable query force-fetching
      ssrForceFetchDelay: 100,
      // Apollo devtools
      connectToDevTools: process.env.NODE_ENV !== 'production'
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all'
      },
      // query: {
      //   fetchPolicy: 'network-only',
      //   errorPolicy: 'all'
      // },
      mutate: {
        errorPolicy: 'all'
      }
    }
  })

  // Manually call this when user log in
  apolloClient.$onLogin = token => {
    localStorage.setItem('apollo-token', token)
    if (wsClient) restartWebsockets(wsClient)
  }

  // Manually call this when user log out
  apolloClient.$onLogout = () => {
    localStorage.removeItem('apollo-token')
    if (wsClient) restartWebsockets(wsClient)
    apolloClient.resetStore()
  }

  return apolloClient
}

// Config
const options = {
  ssr: false,
  base: process.env.VUE_APP_GRAPHQL_ENDPOINT || process.env.VUE_APP_API_URL,
  endpoints: {
    graphql: '/graphql',
    subscription: '/graphql'
  },
  persisting: false,
  subscriptions: false
}

// Create apollo client
export const apolloClient = createApolloClient(options)
